import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * This service handles authentication using Azure MSAL
 * for the camera application
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly destroying$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private broadcastService: MsalBroadcastService
  ) {
    this.storageAuth();
  }

  /**
   * Returns either the user is logged in or don't.
   *
   * @returns boolean
   */
  public isLoggedIn(): boolean {
    return localStorage.getItem(environment.localStorage.userName) !== null;
  }

  /**
   * Clears the session and redirects the user.
   *
   * @returns void
   */
  public logout(): void {
    this.destroySession();
    this.authService.logoutRedirect({
      postLogoutRedirectUri: environment.redirectUrl,
    });
  }

  /**
   * Stored authentication information
   * @returns void
   */
  public storageAuth(): void {
    this.broadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this.destroying$)
      )
      .subscribe({
        next: () => {
          const isAuthenticated =
            this.authService.instance.getAllAccounts().length > 0;

          if (isAuthenticated) {
            const account = this.authService.instance.getAllAccounts()[0] as any;
            const claims =
              account && account.idTokenClaims
                ? (account.idTokenClaims as Record<string, unknown>)
                : ({} as Record<string, unknown>);

            localStorage.setItem(
              environment.localStorage.userName,
              String(account.username || '')
            );
            localStorage.setItem(
              environment.localStorage.userEmail,
              String((claims['email'] as string) || '')
            );
            localStorage.setItem(
              environment.localStorage.userUpn,
              String((claims['upn'] as string) || '')
            );
            localStorage.setItem(
              environment.localStorage.userFirstName,
              String((claims['given_name'] as string) || '')
            );
            localStorage.setItem(
              environment.localStorage.userLastName,
              String((claims['family_name'] as string) || '')
            );
          }
        }
      });
  }

  /**
   * Deletes the stored authentication information of the localStorage.
   *
   * @returns void
   */
  private destroySession(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
    localStorage.removeItem(environment.localStorage.userName);
    localStorage.removeItem(environment.localStorage.userEmail);
    localStorage.removeItem(environment.localStorage.userUpn);
    localStorage.removeItem(environment.localStorage.userFirstName);
    localStorage.removeItem(environment.localStorage.userLastName);
  }
}
